import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import CsvIcon from "./components/icons/CsvIcon";
import FileIcon from "./components/icons/FileIcon";
import TrashIcon from "./components/icons/TrashIcon";
import Topbar from "./components/Topbar";

const backendUrl = process.env.REACT_APP_API_URL; // Get API URL from .env
console.log("Backend URL:", backendUrl); // Debugging

const howItWorksData = [
  {
    heading: "Upload Your Exclusions List",
    description: (
      <>
        Your file should contain a column titled{" "}
        <span style={{ fontWeight: 600, color: "#333" }}>"Address"</span> with
        the addresses you want excluded.
      </>
    ),
    isActive: true,
  },
  {
    heading: "Automatic Filtering",
    description:
      "When you clean a file, any address in your exclusions list is automatically removed.",
    isActive: false,
  },
  {
    heading: "Manage Your List",
    description:
      "You can update or replace your exclusions list anytime to keep it accurate.",
    isActive: false,
  },
];

const Exclusions = () => {
  const [exclusionLists, setExclusionLists] = useState([]);

  // ✅ Retrieve `userId` from localStorage (or state management)
  const userId = localStorage.getItem("userId"); // Ensure the user is logged in and ID is stored

  if (!userId) {
    console.error("User ID is missing!");
  }

  // ✅ Fetch Exclusions from Backend on Page Load and After Deletion
  const fetchExclusions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/exclusions`, {
        params: { user_id: userId },
      });
      setExclusionLists(response.data);
    } catch (error) {
      console.error("Error fetching exclusion lists:", error);
    }
  };

  // ✅ useEffect Hook - Runs on Page Load & When `userId` Changes
  useEffect(() => {
    if (!userId) {
      console.warn("⚠️ No userId found. Skipping exclusions fetch.");
      return;
    }

    const fetchExclusions = async () => {
      try {
        console.log("🔄 Fetching exclusion lists for userId:", userId);

        const response = await axios.get(`${backendUrl}/api/exclusions`, {
          params: { user_id: userId },
        });

        console.log("✅ Exclusions fetched:", response.data);

        setExclusionLists(response.data.length > 0 ? response.data : []);
      } catch (error) {
        console.error("❌ Error fetching exclusion lists:", error);
      }
    };

    fetchExclusions();

    return () => {
      console.log("🧹 Clearing old exclusions on userId change...");
      setExclusionLists([]);
    };
  }, [userId]); // ✅ Re-run when userId changes

  // ✅ Function to Refresh Exclusions After Deletion
  const refreshExclusions = () => {
    fetchExclusions();
  };

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", userId);

      // ✅ Debugging step: Log userId before making the request
      console.log("Uploading exclusion list with user_id:", userId);

      try {
        const response = await axios.post(
          `${backendUrl}/api/exclusions/upload`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (response.status === 200) {
          setExclusionLists([
            ...exclusionLists,
            { id: response.data.id, name: file.name },
          ]);
        }
      } catch (error) {
        console.error("Error uploading exclusion list:", error);
      }
    },
    [exclusionLists, userId],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ".csv",
  });

  // ✅ Handle file deletion
  const handleDelete = async (listName) => {
    // Normalize list name encoding
    const decodedListName = decodeURIComponent(
      listName.trim().replace(/\s/g, "_"),
    );
    console.log("🟢 Attempting to delete:", decodedListName); // Log correct name

    try {
      const response = await axios.delete(
        `${backendUrl}/api/exclusions/delete`,
        {
          params: {
            user_id: userId,
            list_name: decodedListName, // Ensure properly formatted list name
          },
        },
      );

      console.log("✅ Delete Response:", response.data);

      if (response.status === 200) {
        console.log("🟢 Successfully deleted. Refreshing UI...");
        fetchExclusions(); // Refresh UI after deletion
      } else {
        console.error("❌ Failed to delete exclusion list:", response.data);
      }
    } catch (error) {
      console.error("❌ Error deleting exclusion list:", error);
    }
  };

  return (
    <>
      <Topbar />

      <Container maxWidth={"xl"}>
        <Grid
          container
          py={"80px"}
          columnGap={"24px"}
          rowGap={"40px"}
          justifyContent={"space-between"}
          alignItems={"stretch"}
          sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
        >
          <Grid item flexBasis={"507px"}>
            <Typography variant="h1">Exclusions Lists</Typography>

            <Typography
              variant="body4"
              mt={1.5}
              sx={{ textWrap: "balance" }}
              color="#8B8B8B"
            >
              Upload a CSV file containing addresses you want automatically
              removed from your cleaned list. The file must have a column titled{" "}
              <Typography
                variant="inherit"
                color="brand"
                component={"span"}
                fontWeight={600}
              >
                "Address"
              </Typography>
              .
            </Typography>

            <Box mt={"40px"}>
              <Typography variant="h3" fontSize={"20px"}>
                How It Works:
              </Typography>

              <Stepper
                orientation="vertical"
                sx={{ mt: "20px", maxWidth: "374px" }}
              >
                {howItWorksData.map((step, index) => (
                  <Step key={index} active={step.isActive} expanded>
                    <StepLabel>{step.heading}</StepLabel>
                    <StepContent sx={{ pt: "5px" }}>
                      <Typography color="#8B8B8B" variant="body8">
                        {step.description}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Grid>

          <Grid item size={{ xs: 12, md: 6.5 }} pt={{ md: "80px" }}>
            <Box
              {...getRootProps()}
              sx={{
                border: "1px dashed #8B8B8B",
                borderRadius: "10px",
                p: "30px",
                textAlign: "center",
                cursor: "pointer",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                aspectRatio: { lg: "16/8" },
                minHeight: "300px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "30px",
              }}
            >
              <input {...getInputProps()} />
              <FileIcon
                width={53}
                height={53}
                viewBox="0 0 25 24"
                style={{ flexShrink: 0 }}
              />
              <Typography
                fontSize={{ xs: "14px", lg: "24px" }}
                variant="body8"
                component={"p"}
                color="#7B7979"
              >
                <span style={{ fontWeight: 600, color: "#333" }}>
                  Drag & drop file to upload
                </span>{" "}
                <br /> or{" "}
                <span style={{ textDecoration: "underline", color: "#F69133" }}>
                  browse file
                </span>{" "}
                on your computer
              </Typography>

              <Button
                variant="contained"
                color="default"
                sx={{ pointerEvents: "none" }}
              >
                Upload file
              </Button>
            </Box>
            <Typography
              mt={1}
              variant="body4"
              color="#828282"
              display={"flex"}
              justifyContent={"space-between"}
            >
              <span>Format: .CSV</span>
              <span>Max. size: 20Mb</span>
            </Typography>

            {exclusionLists.length > 0 && (
              <Stack spacing={1} mt={3}>
                {exclusionLists.map((list, index) => (
                  <Box
                    key={index}
                    display={"flex"}
                    alignItems={"center"}
                    gap={"20px"}
                    sx={{
                      border: "1px solid #D7D7D7",
                      borderRadius: "10px",
                      p: "10px 20px",
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      borderColor: "#D7D7D7",
                    }}
                  >
                    <CsvIcon style={{ flexShrink: 0 }} />

                    <Box maxWidth={"16.25rem"} flexGrow={1} minWidth={0}>
                      <Typography
                        fontSize={"14px"}
                        variant="body8"
                        component={"p"}
                        color="#333"
                        noWrap
                      >
                        {list.name}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      color="#999"
                      sx={{
                        ":hover": { color: "#333" },
                        m: "0 -5px 0 auto",
                      }}
                      aria-label="delete"
                      onClick={() => handleDelete(list.name)}
                    >
                      <TrashIcon />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Exclusions;
